import { FC } from 'react';
import { Button } from 'antd';
import { TbArrowRight } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import DropdownPanel, { DropdownMenuLabel } from '@/shared/components/dropdowns/DropdownPanel';
import SmallTag from '@/shared/components/tags/SmallTag';

import type { PreviewChange } from '../changes';
import ResourceChangeValue from '../ResourceChangeValue';

interface ChangeDetailsPanelProps {
    activeChangeIndex?: number;
    changes: PreviewChange[];
    description?: string;
    onSelectChange?: (index: number) => void;
    title?: string;
}

const ChangeDetailsPanel: FC<ChangeDetailsPanelProps> = ({
    activeChangeIndex,
    changes,
    description,
    onSelectChange,
    title = 'Change Details',
}) => {
    const { token } = useAntDesignTheme();

    return (
        <DropdownPanel
            contentProps={{ 'aria-label': title, role: 'region' }}
            contentInnerStyle={{ padding: '0.5rem' }}
            header={
                <div style={{ paddingBlock: '0.25rem' }}>
                    <DropdownMenuLabel extra={<SmallTag compact message={changes.length} styles={{ marginLeft: 0 }} />}>
                        {title}
                    </DropdownMenuLabel>
                    {description && (
                        <small
                            style={{ display: 'block', color: token.colorTextTertiary, padding: '0 0.75rem 0.25rem' }}
                        >
                            {description}
                        </small>
                    )}
                </div>
            }
            // viewport-layout-allow: keep the shared floating panel inside narrow viewports.
            style={{ maxWidth: 'calc(100vw - 2rem)' }}
            width='25rem'
        >
            <div style={{ border: `1px dashed ${token.colorBorder}`, borderRadius: token.borderRadius }}>
                {changes.map((change, index) => {
                    const label = change.label ?? `Change ${index + 1}`;
                    const tagLabel =
                        change.type === 'replaced' ? 'Updated' : change.type === 'removed' ? 'Removed' : 'Added';
                    const content = (
                        <span style={{ display: 'grid', gap: '0.5rem', minWidth: 0, width: '100%' }}>
                            <span
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <small style={{ color: token.colorTextTertiary, overflowWrap: 'anywhere' }}>
                                    {label}
                                </small>
                                <SmallTag compact message={tagLabel} styles={{ flexShrink: 0, margin: 0 }} />
                            </span>
                            <span
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    gap: '0.5rem',
                                    minWidth: 0,
                                }}
                            >
                                {change.type !== 'added' && (
                                    <ResourceChangeValue
                                        strikethrough
                                        tone='removed'
                                        value={change.before || 'Blank content'}
                                    />
                                )}
                                {change.type === 'replaced' && (
                                    <TbArrowRight
                                        aria-label='Changed to'
                                        size='1rem'
                                        style={{ flex: 'none', color: token.colorTextTertiary }}
                                    />
                                )}
                                {change.type !== 'removed' && (
                                    <ResourceChangeValue tone='added' value={change.after || 'Blank content'} />
                                )}
                            </span>
                        </span>
                    );
                    return (
                        <div
                            key={change.id}
                            style={{ borderTop: index ? `1px solid ${token.colorBorderSecondary}` : undefined }}
                        >
                            {onSelectChange ? (
                                <Button
                                    aria-current={index === activeChangeIndex ? 'true' : undefined}
                                    aria-label={`Review ${label}`}
                                    block
                                    onClick={() => onSelectChange(index)}
                                    style={{
                                        height: 'auto',
                                        paddingBlock: '0.75rem',
                                        textAlign: 'left',
                                        whiteSpace: 'normal',
                                        background: index === activeChangeIndex ? token.colorFillTertiary : undefined,
                                    }}
                                    type='text'
                                >
                                    {content}
                                </Button>
                            ) : (
                                <div style={{ padding: '0.75rem' }}>{content}</div>
                            )}
                        </div>
                    );
                })}
            </div>
        </DropdownPanel>
    );
};

export default ChangeDetailsPanel;
